/**
 * Analytics helper — wraps Plausible custom events.
 * Safe to call before script loads (queues via plausible.q stub).
 * No-op if plausible script blocked or in dev.
 *
 * Standard goal names (keep stable for funnels):
 *   "Signup Started", "Signup Completed", "KYC Submitted",
 *   "Project Posted", "Bid Placed", "Bid Accepted",
 *   "Gig Published", "Top-up Initiated", "Top-up Completed",
 *   "Withdrawal Requested", "Withdrawal Approved", "Message Sent"
 */
export function track(eventName, props = {}) {
  try {
    if (typeof window === 'undefined') return;
    if (window.plausible) {
      window.plausible(eventName, { props });
    }
  } catch (e) { /* silently ignore */ }
}

/** Meta Pixel standard event (no-op when the pixel is not configured). */
export function fb(eventName, params = {}, standard = true) {
  try {
    if (typeof window === 'undefined' || !window.fbq) return;
    window.fbq(standard ? 'track' : 'trackCustom', eventName, params);
  } catch (e) { /* silently ignore */ }
}

/** Fire a Meta Pixel PageView on SPA route changes. */
export function fbPageView() { fb('PageView'); }

// Convenience wrappers for the funnel events
export const Analytics = {
  viewGig: (slug, price) => fb('ViewContent', { content_type: 'product', content_ids: [slug], value: Number(price) || 0, currency: 'USD' }),
  orderPlaced: (slug, price) => fb('Purchase', { content_type: 'product', content_ids: [slug], value: Number(price) || 0, currency: 'USD' }),
  contactSeller: () => fb('Contact'),
  search: (q) => fb('Search', { search_string: String(q || '').slice(0, 80) }),
  signupStarted: (method) => { track('Signup Started', { method }); fb('Lead', { content_name: 'signup_started' }); },
  signupCompleted: (role) => { track('Signup Completed', { role }); fb('CompleteRegistration', { content_name: role, status: true }); },
  kycSubmitted: () => track('KYC Submitted'),
  projectPosted: (category) => { track('Project Posted', { category }); fb('SubmitApplication', { content_category: category }); },
  bidPlaced: (project_id) => { track('Bid Placed', { project_id: String(project_id) }); fb('Lead', { content_name: 'proposal' }); },
  bidAccepted: (project_id) => track('Bid Accepted', { project_id: String(project_id) }),
  gigPublished: (category) => { track('Gig Published', { category }); fb('SubmitApplication', { content_category: category, content_name: 'gig' }); },
  topupInitiated: (amount, gateway) => { track('Top-up Initiated', { amount: String(amount), gateway }); fb('InitiateCheckout', { value: Number(amount) || 0, currency: 'USD' }); },
  topupCompleted: (amount, gateway) => { track('Top-up Completed', { amount: String(amount), gateway }); fb('Purchase', { value: Number(amount) || 0, currency: 'USD' }); },
  withdrawalRequested: (amount, method) => track('Withdrawal Requested', { amount: String(amount), method }),
  messageSent: () => track('Message Sent'),
};

export default Analytics;
